import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Users, Plus, X, Loader2, CheckCircle2, Upload, Pencil, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_ORG = {
  display_name: '', bio: '', website: '', primary_city: '',
  instagram: '', tiktok: '', facebook: '', twitter: '', youtube: '', logo_url: '',
  gallery_images: [],
};

/**
 * OrganizerPicker — lives in the event editor's Details tab.
 * Shows the organizer profile attached to the event (organization_id),
 * lets admins pick another one, or create/edit a brand profile inline.
 */
export const OrganizerPicker = ({ organizationId, onChange, token }) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [form, setForm] = useState(EMPTY_ORG);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations?limit=200`, auth());
      setOrgs(res.data || []);
    } catch { /* non-admin or error — keep empty */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const current = orgs.find(o => o.organization_id === organizationId);

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, logo_url: `${API}/files/${res.data.storage_path}` }));
    } catch { toast.error('Logo upload failed'); }
    setLogoUploading(false);
  };

  const handleGalleryUpload = async (files) => {
    if (!files?.length) return;
    const room = 8 - (form.gallery_images || []).length;
    if (room <= 0) { toast.error('Gallery is full — 8 photos max'); return; }
    setGalleryUploading(true);
    try {
      const uploads = Array.from(files).slice(0, room).map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        return `${API}/files/${res.data.storage_path}`;
      });
      const urls = await Promise.all(uploads);
      setForm(f => ({ ...f, gallery_images: [...(f.gallery_images || []), ...urls].slice(0, 8) }));
    } catch { toast.error('Some photos failed to upload'); }
    setGalleryUploading(false);
  };

  const openCreate = () => {
    setEditingOrgId(null);
    setForm(EMPTY_ORG);
    setShowForm(true);
  };

  const openEdit = (org) => {
    setEditingOrgId(org.organization_id);
    setForm({
      display_name: org.display_name || '', bio: org.bio || '', website: org.website || '',
      primary_city: org.primary_city || '', instagram: org.instagram || '', tiktok: org.tiktok || '',
      facebook: org.facebook || '', twitter: org.twitter || '', youtube: org.youtube || '',
      logo_url: org.logo_url || '',
      gallery_images: org.gallery_images || [],
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.display_name.trim()) { toast.error('Organizer name is required'); return; }
    setSaving(true);
    try {
      if (editingOrgId) {
        await axios.put(`${API}/admin/organizations/${editingOrgId}`, form, auth());
        toast.success('Organizer profile updated');
      } else {
        const res = await axios.post(`${API}/admin/organizations`, form, auth());
        onChange(res.data.organization_id);
        toast.success(`"${res.data.display_name}" created and attached to this event`);
      }
      setShowForm(false);
      setPicking(false);
      fetchOrgs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save organizer profile');
    }
    setSaving(false);
  };

  const filtered = orgs.filter(o =>
    !search || (o.display_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const socialField = (key, placeholder) => (
    <div className="space-y-1" key={key}>
      <Label className="text-[11px] capitalize">{key}</Label>
      <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder} className="h-8 text-xs" data-testid={`org-form-${key}`} />
    </div>
  );

  return (
    <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-4 space-y-3" data-testid="organizer-picker">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-fuchsia-500/10 flex-shrink-0">
          <Users className="h-4 w-4 text-fuchsia-400" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold">Organizer Profile</p>
          <p className="text-xs text-muted-foreground">Shown on the public event page's Organiser tab — name, logo, bio, website and socials. Every event should have one.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading profiles…</div>
      ) : current ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-background/50 p-3" data-testid="organizer-current">
          {current.logo_url ? (
            <img src={current.logo_url} alt={current.display_name} className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-fuchsia-500/15 flex items-center justify-center text-fuchsia-400 font-bold">
              {(current.display_name || '?')[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold flex items-center gap-1.5 truncate">
              {current.display_name}
              {current.is_verified && <CheckCircle2 className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">
              {[current.primary_city, current.bio].filter(Boolean).join(' · ') || 'No bio yet — click Edit to add one'}
            </p>
          </div>
          <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => openEdit(current)} data-testid="organizer-edit-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => setPicking(!picking)} data-testid="organizer-change-btn">
            Change
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-3" data-testid="organizer-missing">
          <p className="text-xs text-amber-400">No organizer attached — the public Organiser tab will look empty.</p>
          <Button type="button" size="sm" className="h-8 bg-fuchsia-500 hover:bg-fuchsia-600 text-white" onClick={() => setPicking(true)} data-testid="organizer-attach-btn">
            Attach organizer
          </Button>
        </div>
      )}

      {picking && !showForm && (
        <div className="space-y-2 rounded-lg border border-border bg-background/50 p-3" data-testid="organizer-list">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organizer profiles…" className="h-8 pl-8 text-xs" data-testid="organizer-search" />
            </div>
            <Button type="button" size="sm" className="h-8 bg-fuchsia-500 hover:bg-fuchsia-600 text-white" onClick={openCreate} data-testid="organizer-new-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New profile
            </Button>
          </div>
          <div className="max-h-52 overflow-y-auto space-y-1">
            {filtered.length === 0 && <p className="text-xs text-muted-foreground py-3 text-center">No profiles match — create one.</p>}
            {filtered.map(o => (
              <button
                key={o.organization_id}
                type="button"
                onClick={() => { onChange(o.organization_id); setPicking(false); toast.success(`Organizer set to "${o.display_name}"`); }}
                className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-accent/50 transition-colors ${o.organization_id === organizationId ? 'bg-fuchsia-500/10 border border-fuchsia-500/30' : ''}`}
                data-testid={`organizer-option-${o.organization_id}`}
              >
                {o.logo_url ? (
                  <img src={o.logo_url} alt="" className="h-7 w-7 rounded object-cover" />
                ) : (
                  <div className="h-7 w-7 rounded bg-muted flex items-center justify-center text-xs font-bold">{(o.display_name || '?')[0]}</div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{o.display_name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{[o.primary_city, o.email].filter(Boolean).join(' · ')}</p>
                </div>
                {o.is_verified && <Badge className="bg-blue-500/15 text-blue-400 text-[9px] flex-shrink-0">Verified</Badge>}
              </button>
            ))}
          </div>
          {organizationId && (
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-rose-400 w-full" onClick={() => { onChange(null); setPicking(false); }} data-testid="organizer-detach-btn">
              <X className="h-3 w-3 mr-1" /> Detach organizer from this event
            </Button>
          )}
        </div>
      )}

      {showForm && (
        <div className="space-y-3 rounded-lg border border-fuchsia-500/30 bg-background/50 p-3" data-testid="organizer-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold">{editingOrgId ? 'Edit organizer profile' : 'New organizer profile'}</p>
            <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={() => setShowForm(false)} data-testid="organizer-form-close">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[11px]">Organizer / brand name *</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="e.g. Denver Wine Mixer" className="h-8 text-xs" data-testid="org-form-name" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[11px]">Bio</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Who you are and what kind of events you throw…" className="min-h-[70px] text-xs" data-testid="org-form-bio" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…" className="h-8 text-xs" data-testid="org-form-website" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">City</Label>
              <Input value={form.primary_city} onChange={(e) => setForm({ ...form, primary_city: e.target.value })}
                placeholder="denver" className="h-8 text-xs" data-testid="org-form-city" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[11px]">Logo</Label>
              <div className="flex items-center gap-2">
                {form.logo_url && <img src={form.logo_url} alt="logo" className="h-9 w-9 rounded-lg object-cover border border-border" />}
                <label className="flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs cursor-pointer hover:bg-accent/50 transition-colors">
                  {logoUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {form.logo_url ? 'Replace logo' : 'Upload logo'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} data-testid="org-form-logo-input" />
                </label>
              </div>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[11px]">Past event gallery <span className="text-muted-foreground">({(form.gallery_images || []).length}/8 — builds trust on your public profile)</span></Label>
              <div className="flex flex-wrap gap-2">
                {(form.gallery_images || []).map((url, i) => (
                  <div key={i} className="relative group" data-testid={`org-form-gallery-${i}`}>
                    <img src={url} alt={`gallery ${i + 1}`} className="h-14 w-14 rounded-lg object-cover border border-border" />
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, x) => x !== i) }))}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`org-form-gallery-remove-${i}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
                {(form.gallery_images || []).length < 8 && (
                  <label className="h-14 w-14 rounded-lg border border-dashed border-fuchsia-500/40 flex items-center justify-center cursor-pointer hover:bg-fuchsia-500/5 transition-colors">
                    {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin text-fuchsia-400" /> : <Plus className="h-4 w-4 text-fuchsia-400" />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleGalleryUpload(e.target.files)} data-testid="org-form-gallery-input" />
                  </label>
                )}
              </div>
            </div>
            {socialField('instagram', '@handle or URL')}
            {socialField('tiktok', '@handle or URL')}
            {socialField('facebook', 'page URL')}
            {socialField('twitter', '@handle or URL')}
            {socialField('youtube', 'channel URL')}
          </div>
          <Button type="button" size="sm" className="h-8 w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white" onClick={handleSave} disabled={saving} data-testid="org-form-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {editingOrgId ? 'Save changes' : 'Create & attach to this event'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrganizerPicker;
